import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { useLang } from "@/lang";
import { MaskLines, Reveal, Crosshair } from "@/components/Reveal";
import { PHONE, PHONE_TEL, EMAIL } from "@/content";

const HERO_BG =
    "https://images.unsplash.com/photo-1621905251918-48416bd8575a?q=85&w=2000&auto=format&fit=crop";
const BAND_IMG =
    "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const CREW_IMG_1 =
    "https://images.pexels.com/photos/16509869/pexels-photo-16509869.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700";
const CREW_IMG_2 =
    "https://images.unsplash.com/photo-1676210134190-3f2c0d5cf58d?q=85&w=1200&auto=format&fit=crop";

function Band() {
    const { c } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
    return (
        <section ref={ref} className="relative flex min-h-[85svh] items-end overflow-hidden" data-testid="about-hero">
            <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
                <img src={HERO_BG} alt="" className="h-full w-full scale-110 object-cover" />
                <div className="absolute inset-0 bg-ink/60" />
                <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-transparent to-ink" />
            </motion.div>
            <div className="container-x pb-20 pt-40">
                <Reveal>
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky">
                        <Crosshair className="text-sky" />
                        {c.about.eyebrow}
                    </div>
                </Reveal>
                <h1 className="mt-6 text-[clamp(2.8rem,8vw,7rem)] font-light leading-[1.03] tracking-[-0.02em] text-paper">
                    <MaskLines mount lines={c.about.lines} step={0.13} delay={0.2} />
                </h1>
            </div>
        </section>
    );
}

export default function About() {
    const { c } = useLang();
    return (
        <>
            <Band />

            <section className="container-x grid gap-14 py-28 md:grid-cols-12 md:py-36" data-testid="about-story">
                <div className="md:col-span-6">
                    <Reveal>
                        <h2 className="text-4xl font-light tracking-tight text-ink sm:text-5xl">
                            {c.about.storyTitle}
                        </h2>
                        <p className="mt-8 text-base leading-relaxed text-ink-soft sm:text-lg" dangerouslySetInnerHTML={{ __html: c.about.storyBody }} />
                    </Reveal>
                </div>
                <div className="md:col-span-5 md:col-start-8">
                    {c.about.timeline.map((t, i) => (
                        <Reveal key={t.y} delay={i * 0.06}>
                            <div className="group flex gap-8 border-b border-line py-6 transition-colors hover:bg-paper" data-testid={`about-timeline-${i + 1}`}>
                                <span className="w-14 shrink-0 font-mono text-sm font-medium tracking-[0.15em] text-sky-text">
                                    {t.y}
                                </span>
                                <span className="text-sm leading-relaxed text-ink-soft sm:text-base">
                                    {t.t}
                                </span>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="relative h-[60vh] overflow-hidden md:h-[75vh]" data-testid="about-band">
                <motion.img
                    src={BAND_IMG}
                    alt=""
                    initial={{ scale: 1.15 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                    className="h-full w-full object-cover"
                />
            </section>

            <section className="container-x py-28 md:py-36" data-testid="about-values">
                <Reveal>
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky-text">
                        <span className="h-px w-10 bg-sky-text/50" />
                        {c.about.valuesTitle}
                    </div>
                </Reveal>
                <div className="mt-12 border-t border-line">
                    {c.about.values.map((v, i) => (
                        <Reveal key={v.t} delay={i * 0.06}>
                            <div className="grid gap-4 border-b border-line py-10 md:grid-cols-12 md:items-baseline" data-testid={`about-value-${i + 1}`}>
                                <span className="font-light text-5xl text-mist/70 md:col-span-1">0{i + 1}</span>
                                <h3 className="text-2xl font-light tracking-tight text-ink md:col-span-4 sm:text-3xl">
                                    {v.t}
                                </h3>
                                <p className="max-w-xl text-base leading-relaxed text-ink-soft md:col-span-6">
                                    {v.d}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="container-x pb-28 md:pb-36" data-testid="about-crew">
                <div className="grid items-center gap-14 lg:grid-cols-2">
                    <div>
                        <Reveal>
                            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky-text">
                                <span className="h-px w-10 bg-sky-text/50" />
                                {c.about.crewLabel}
                            </div>
                        </Reveal>
                        <MaskLines
                            lines={[c.about.crewTitle]}
                            className="mt-6 block text-4xl font-light tracking-tight text-ink sm:text-5xl"
                        />
                        <Reveal delay={0.15}>
                            <p className="mt-8 max-w-lg text-base leading-relaxed text-ink-soft sm:text-lg" dangerouslySetInnerHTML={{ __html: c.about.crewBody }} />
                        </Reveal>
                        <Reveal delay={0.25}>
                            <div className="mt-10 flex flex-wrap gap-2.5">
                                {c.about.certs.map((cert) => (
                                    <span
                                        key={cert}
                                        className="rounded-full border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft"
                                        data-testid="about-cert-badge"
                                    >
                                        {cert}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                    <div className="grid grid-cols-2 gap-5">
                        <Reveal delay={0.1}>
                            <div className="overflow-hidden rounded-3xl border border-line">
                                <img
                                    src={CREW_IMG_1}
                                    alt={c.about.crewTitle.replace(/<[^>]+>/g, "")}
                                    className="aspect-[3/4] h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                                    data-testid="about-crew-image-1"
                                />
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="mt-10 overflow-hidden rounded-3xl border border-line">
                                <img
                                    src={CREW_IMG_2}
                                    alt={c.about.crewTitle.replace(/<[^>]+>/g, "")}
                                    className="aspect-[3/4] h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                                    data-testid="about-crew-image-2"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-ink" data-testid="about-cta">
                <div className="container-x flex flex-col items-center gap-8 py-20 text-center md:flex-row md:justify-between md:text-left">
                    <MaskLines
                        lines={c.contact.status.split(". ").map((s, i, a) => (i < a.length - 1 ? s + "." : s))}
                        className="text-3xl font-light tracking-tight text-paper sm:text-4xl"
                    />
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Link
                            to="/contact"
                            data-testid="about-contact-link"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-paper px-8 py-4 text-base font-semibold text-ink transition-colors hover:bg-sky"
                        >
                            {c.nav.contact}
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                        <a
                            href={`mailto:${EMAIL}`}
                            data-testid="about-email-cta"
                            className="flex items-center justify-center gap-3 rounded-full border border-paper/30 px-8 py-4 font-mono text-sm text-paper transition-colors hover:border-paper hover:bg-paper/10"
                        >
                            <Mail className="h-4 w-4 text-sky" />
                            {EMAIL}
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
}
